import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangleIcon } from "lucide-react";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { v4 } from "uuid";

import { BasicTooltip } from "../ui/Tooltip";

import { PaymentIssueBanner } from "@/components/PaymentIssueBanner";
import { SubscriptionStatusBanner } from "@/components/ui/SubscriptionStatusBanner";
import { TrialPill } from "@/components/ui/SubscriptionStatusBanner/TrialPill";
import { MAINTENANCE_BANNER_MESSAGE } from "@/constants";
import { useOrganizationSubscriptionData } from "@/providers/OrganizationSubscriptionProvider";

const getHeaderText = (planName: string, isTrialPlan: boolean) =>
  `You're currently using the ${isTrialPlan ? `${planName} Trial` : "Free"} Plan`;

export const MaintenanceBanner = () => {
  if (!MAINTENANCE_BANNER_MESSAGE) {
    return null;
  }

  return (
    <div className="flex h-8 items-center border-b  border-neutral-200 bg-amber-50/50 px-6">
      <div className="flex items-center gap-2">
        <AlertTriangleIcon className="h-3.5 w-3.5 text-amber-600" />
        <span className="text-xs text-neutral-700">{MAINTENANCE_BANNER_MESSAGE}</span>
      </div>
    </div>
  );
};

const POSTMORTEM_MESSAGE_DATE = import.meta.env.REACT_APP_POSTMORTEM_MESSAGE_DATE;
const POSTMORTEM_MESSAGE_URL = import.meta.env.REACT_APP_POSTMORTEM_MESSAGE_URL;

const PostmortemMessage = () => (
  <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-6 py-2 text-sm text-amber-700">
    <AlertTriangleIcon className="h-3.5 w-3.5 text-amber-600" />
    We had an outage on {moment(POSTMORTEM_MESSAGE_DATE).format("MMMM D, YYYY")}. We are sorry for
    the inconvenience.{" "}
    <a
      href={POSTMORTEM_MESSAGE_URL}
      target="_blank"
      className="cursor-pointer text-blue-500 hover:text-blue-600"
      rel="noreferrer"
    >
      What happened?
    </a>
  </div>
);

const Header = () => {
  const {
    isLoadingSubscriptionStatus,
    isOrgOwner,
    status,
    hasActiveSubscription,
    isTrialPlan,
    isFreePlan,
    planName,
    hasPaymentIssue,
  } = useOrganizationSubscriptionData();
  const isBillingPage = window.location.pathname.includes("billing");

  const { sessionId } = useParams();

  const [defaultPlaygroundSessionId, setDefaultPlaygroundSessionId] = useState<string>(
    sessionId ?? v4(),
  );

  useEffect(() => {
    if (window.location.pathname.includes(`playground/${defaultPlaygroundSessionId}`)) {
      return;
    }
    setDefaultPlaygroundSessionId(v4());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  const [showPricingModal, setShowPricingModal] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  if (import.meta.env.REACT_APP_POSTMORTEM_MESSAGE === "true") {
    return <PostmortemMessage />;
  }

  const isLegacyPlan = planName?.toLocaleLowerCase()?.includes("legacy");

  const shouldOnlyShowMaintenanceBanner =
    (hasActiveSubscription && (!isFreePlan || isBillingPage)) ||
    isBillingPage ||
    isLoadingSubscriptionStatus ||
    (status === "canceled" && isLegacyPlan) ||
    hasPaymentIssue;
  return (
    <motion.div
      ref={headerRef}
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
      className="relative w-full overflow-hidden bg-gradient-to-r from-neutral-50 to-white"
    >
      <MaintenanceBanner />
      <AnimatePresence initial={false}>
        {hasPaymentIssue && !isBillingPage && <PaymentIssueBanner />}
      </AnimatePresence>
      <AnimatePresence initial={false}>
        {!shouldOnlyShowMaintenanceBanner && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height: "auto",
              opacity: 1,
            }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="flex items-center justify-between overflow-hidden px-6"
          >
            <div className="flex flex-1 items-center justify-between py-2">
              <div className="min-[900px]:flex-1 flex min-w-0 items-center gap-2">
                <span className="text-xs text-neutral-500">
                  {getHeaderText(planName || "", isTrialPlan)}
                </span>
              </div>
              <div className="min-[900px]:flex-1 min-[900px]:justify-end ml-4 flex flex-shrink-0 items-center gap-2">
                {isTrialPlan && <TrialPill />}
                <BasicTooltip
                  shouldShow={!isOrgOwner}
                  content={!isOrgOwner ? "Only owners are allowed to upgrade the plan." : ""}
                >
                  <span>
                    <SubscriptionStatusBanner
                      buttonClassName="h-7"
                      disabled={!isOrgOwner}
                      buttonText=""
                      showPricingModal={showPricingModal}
                      setShowPricingModal={setShowPricingModal}
                    />
                  </span>
                </BasicTooltip>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Header;
